// 兼职人员 新增/编辑 共用弹窗表单
// - 可编辑字段：姓名、几个微信、微信昵称、微信号、拉新添加工资、拉新进群工资、是否满五天、
//   是否在做、备注、负责客服、角色、ta的团长、入职时间（后期放宽可改）
// - 本人登录账号：新增时必填且唯一（后端校验）；编辑时只读展示，修改请走「重置账号密码」
// - 工资变化时后端自动生成新的工资版本号；任一字段时间线由后端记录
// - 负责客服：弹出 EmployeePicker 选择明道云员工；ta的团长：弹出全部兼职人员列表选择（保存 leaderId）
import React, { useEffect, useState } from 'react';
import {
  Dialog, Button, Space, Input, Select, DatePicker, Textarea, MessagePlugin, Table, Pagination,
} from 'tdesign-react';
import { parttimeApi } from '../../api/parttime';
import EmployeePicker from './EmployeePicker';

const ROLE_OPTS = [
  { label: '团长', value: 'LEADER' },
  { label: '个人', value: 'PERSON' },
  { label: '团长下个人', value: 'LEADER_SUB' },
];
const YES_NO_OPTS = [
  { label: '是', value: '是' },
  { label: '否', value: '否' },
];
const ROLE_LABEL = { LEADER: '团长', PERSON: '个人', LEADER_SUB: '团长下个人' };

/** 团长选择弹窗：每页 50 条（后端分页） */
const LEADER_PAGE_SIZE = 50;

/** 弹窗内单行表单字段布局：label + 控件（半行宽）。
 * 注意：必须定义在模块顶层而非组件函数体内——否则每次渲染都会生成新的组件类型，
 * React 会将所有输入框卸载重挂，导致每输入一个字符就失焦 */
const Field = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 'calc(50% - 8px)', minWidth: 300 }}>
    <span style={{ width: 92, textAlign: 'right', color: '#374151', fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0 }}>{label}</span>
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
  </div>
);

/** 今天的 yyyy-MM-dd（新增时入职时间默认值） */
const todayStr = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

/** 空表单初始值 */
const emptyForm = (mode) => ({
  userId: null,
  phone: '',
  name: '',
  role: '',
  leaderId: null,
  leaderName: '',
  leaderLogin: '',
  wechatCount: '',
  wechatNick: '',
  wechatId: '',
  addWage: '',
  groupWage: '',
  isFullFive: '',
  isWorking: '',
  applyNote: '',
  serviceKf: '',
  serviceKfId: '',
  entryTime: mode === 'create' ? todayStr() : '',
});

/**
 * 兼职人员 新增/编辑 弹窗
 * props:
 *  - visible: 是否展示
 *  - mode: 'create' | 'edit'
 *  - initial: 编辑时的行对象（create 时传 null）
 *  - onCancel(): 关闭
 *  - onSaved(row): 保存成功后回调（父组件刷新列表）
 */
export default function ParttimeUserFormModal({ visible, mode = 'create', initial = null, onCancel, onSaved }) {
  const [form, setForm] = useState(() => emptyForm(mode));
  const [saving, setSaving] = useState(false);
  // 团长候选（当前页兼职人员；后端分页返回）
  const [leaderOpts, setLeaderOpts] = useState([]);
  const [leaderDlg, setLeaderDlg] = useState(false);
  const [leaderLoading, setLeaderLoading] = useState(false);
  // 团长选择弹窗（后端分页，每页 50 条）
  const [leaderPage, setLeaderPage] = useState(1);
  const [leaderTotal, setLeaderTotal] = useState(0);
  // 负责客服员工选择弹窗
  const [empDlg, setEmpDlg] = useState(false);

  // 弹窗打开时：按模式初始化表单；编辑回填行数据（含工资版本最新价、团长名、客服名）
  useEffect(() => {
    if (!visible) return;
    if (mode === 'create') {
      setForm(emptyForm('create'));
    } else if (initial) {
      const w = initial.wage || {};
      setForm({
        userId: initial.id,
        phone: initial.phone || '',
        name: initial.name || '',
        role: initial.role || '',
        leaderId: initial.leaderId || null,
        leaderName: initial.leaderName || '',
        leaderLogin: initial.leaderLogin || '',
        wechatCount: initial.wechatCount == null ? '' : String(initial.wechatCount),
        wechatNick: initial.wechatNick || '',
        wechatId: initial.wechatId || '',
        addWage: w.addWage == null ? '' : String(w.addWage),
        groupWage: w.groupWage == null ? '' : String(w.groupWage),
        isFullFive: initial.isFullFive || '',
        isWorking: initial.isWorking || '',
        applyNote: initial.applyNote || '',
        serviceKf: initial.serviceKf || '',
        serviceKfId: initial.serviceKfId || '',
        entryTime: initial.entryTime || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, mode]);

  /** 更新单个表单字段 */
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /** 加载团长候选某一页（后端分页，每页 50 条；编辑时排除自己） */
  const loadLeader = async (p) => {
    try {
      setLeaderLoading(true);
      const res = (await parttimeApi.userOptions({
        page: p,
        size: LEADER_PAGE_SIZE,
        ...(mode === 'edit' && form.userId ? { excludeId: form.userId } : {}),
      })) || {};
      setLeaderOpts(res.rows || []);
      setLeaderTotal(res.total || 0);
      setLeaderPage(p);
    } catch (e) {
      MessagePlugin.error(e.message);
      setLeaderDlg(false);
    } finally {
      setLeaderLoading(false);
    }
  };

  /** 打开团长选择弹窗：从第 1 页开始加载 */
  const openLeaderDlg = async () => {
    setLeaderDlg(true);
    await loadLeader(1);
  };

  /** 选择某兼职为团长：记录 id + 姓名 + 登录账号 */
  const chooseLeader = (u) => {
    setForm((f) => ({
      ...f,
      leaderId: u.id,
      leaderName: u.name || u.phone,
      leaderLogin: u.phone,
    }));
    setLeaderDlg(false);
  };

  /** 保存校验：必填/格式，与后端 create/update 约束一致 */
  const validate = () => {
    if (!form.phone || !form.phone.trim()) return '请填写本人登录账号';
    if (!form.name || !form.name.trim()) return '请填写姓名';
    if (!form.role) return '请选择角色';
    if (form.role === 'LEADER_SUB' && !form.leaderId) return '角色为「团长下个人」时必须选择 ta的团长';
    if (form.addWage === '' || form.addWage == null) return '请填写拉新添加工资';
    if (form.groupWage === '' || form.groupWage == null) return '请填写拉新进群工资';
    const add = Number(form.addWage);
    const grp = Number(form.groupWage);
    if (Number.isNaN(add) || add < 0) return '拉新添加工资格式不正确';
    if (Number.isNaN(grp) || grp < 0) return '拉新进群工资格式不正确';
    return '';
  };

  /** 点击保存：构造载荷调用 create/update 接口（工资变化由后端生成新价格版本号） */
  const doSave = async () => {
    const err = validate();
    if (err) { MessagePlugin.warning(err); return; }
    const payload = {
      phone: form.phone.trim(),
      name: form.name.trim(),
      role: form.role,
      // 角色不是「团长下个人」时不带团长
      ...(form.role === 'LEADER_SUB' && form.leaderId ? { leaderId: form.leaderId } : { leaderId: null }),
      wechatCount: form.wechatCount === '' ? null : form.wechatCount,
      wechatNick: form.wechatNick.trim() || null,
      wechatId: form.wechatId.trim() || null,
      addWage: String(form.addWage),
      groupWage: String(form.groupWage),
      isFullFive: form.isFullFive || null,
      isWorking: form.isWorking || null,
      applyNote: form.applyNote.trim() || null,
      serviceKf: form.serviceKf || null,
      serviceKfId: form.serviceKfId || null,
      entryTime: form.entryTime || null,
    };
    setSaving(true);
    try {
      let row;
      if (mode === 'create') {
        row = await parttimeApi.createUser(payload);
        MessagePlugin.success('新增成功（待兼职在小程序注册后审核）');
      } else {
        row = await parttimeApi.updateUser({ ...payload, userId: form.userId });
        MessagePlugin.success('保存成功');
      }
      onCancel();
      onSaved && onSaved(row);
    } catch (e) {
      MessagePlugin.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        header={mode === 'create' ? '新增兼职人员' : '编辑兼职人员'}
        visible={!!visible}
        width="860px"
        closeOnOverlayClick={false}
        onClose={onCancel}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button theme="default" variant="text" size="small" disabled={saving} onClick={onCancel}>取消</Button>
              <Button theme="primary" variant="text" size="small" loading={saving} disabled={saving} onClick={doSave}>保存</Button>
            </Space>
          </div>
        }
      >
        {mode === 'create' ? (
          <p style={{ margin: '0 0 14px', color: '#888', fontSize: 12 }}>
            新增后状态为「未注册」，需兼职人员在小程序用该登录账号注册后由客服审核
          </p>
        ) : (
          <p style={{ margin: '0 0 14px', color: '#888', fontSize: 12 }}>
            本人登录账号不支持在此修改；如需修改请使用列表中的「重置账号密码」
          </p>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 0' }}>
          <Field label="本人登录账号">
            {mode === 'create' ? (
              <Input value={form.phone} placeholder="兼职人员小程序登录账号" onChange={(v) => set('phone', v)} />
            ) : (
              <Input value={form.phone} disabled />
            )}
          </Field>
          <Field label="入职时间">
            <DatePicker format="YYYY-MM-DD" value={form.entryTime} placeholder="入职时间" clearable style={{ width: '100%' }} onChange={(v) => set('entryTime', v || '')} />
          </Field>
          <Field label="姓名">
            <Input value={form.name} placeholder="姓名" onChange={(v) => set('name', v)} />
          </Field>
          <Field label="角色">
            <Select value={form.role} options={ROLE_OPTS} placeholder="团长/个人/团长下个人" style={{ width: '100%' }}
              onChange={(v) => setForm((f) => ({ ...f, role: v, ...(v !== 'LEADER_SUB' ? { leaderId: null, leaderName: '', leaderLogin: '' } : {}) }))} />
          </Field>
          <Field label="几个微信">
            <Input value={form.wechatCount} placeholder="如：2" onChange={(v) => set('wechatCount', v)} />
          </Field>
          <Field label="微信号">
            <Input value={form.wechatId} placeholder="微信号" onChange={(v) => set('wechatId', v)} />
          </Field>
          <Field label="微信昵称">
            <Input value={form.wechatNick} placeholder="微信昵称" onChange={(v) => set('wechatNick', v)} />
          </Field>
          <Field label="是否满五天">
            <Select value={form.isFullFive} options={YES_NO_OPTS} placeholder="是/否" clearable style={{ width: '100%' }} onChange={(v) => set('isFullFive', v || '')} />
          </Field>
          <Field label="拉新添加工资">
            <Input value={form.addWage} placeholder="数据分配时价格（元）" onChange={(v) => set('addWage', v)} />
          </Field>
          <Field label="拉新进群工资">
            <Input value={form.groupWage} placeholder="数据分配时价格（元）" onChange={(v) => set('groupWage', v)} />
          </Field>
          <Field label="是否在做">
            <Select value={form.isWorking} options={YES_NO_OPTS} placeholder="是/否" clearable style={{ width: '100%' }} onChange={(v) => set('isWorking', v || '')} />
          </Field>
          <Field label="负责客服">
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <Button theme="primary" variant="text" size="small" onClick={() => setEmpDlg(true)}
                style={{ maxWidth: '100%', overflow: 'hidden' }}>
                {form.serviceKf ? form.serviceKf : '点击选择客服'}
              </Button>
              {form.serviceKf ? (
                <Button theme="danger" variant="text" size="small" onClick={() => setForm((f) => ({ ...f, serviceKf: '', serviceKfId: '' }))}>
                  清除
                </Button>
              ) : null}
            </div>
          </Field>
          <Field label="ta的团长">
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <Button theme="primary" variant="text" size="small" disabled={form.role !== 'LEADER_SUB'} onClick={openLeaderDlg}
                style={{ maxWidth: '100%', overflow: 'hidden' }}>
                {form.leaderId ? `${form.leaderName || ''}（${form.leaderLogin || ''}）` : (form.role === 'LEADER_SUB' ? '点击选择团长' : '仅「团长下个人」需设置')}
              </Button>
              {form.leaderId ? (
                <Button theme="danger" variant="text" size="small" onClick={() => setForm((f) => ({ ...f, leaderId: null, leaderName: '', leaderLogin: '' }))}>
                  清除
                </Button>
              ) : null}
            </div>
          </Field>
          <div style={{ width: '100%' }}>
            <span style={{ display: 'inline-block', width: 92, textAlign: 'right', marginRight: 8, color: '#374151', fontSize: 13 }}>备注</span>
            <Textarea value={form.applyNote} placeholder="备注（选填）" autosize={{ minRows: 2, maxRows: 6 }} style={{ width: 'calc(100% - 108px)', verticalAlign: 'top' }} onChange={(v) => set('applyNote', v)} />
          </div>
        </div>
      </Dialog>

      {/* 团长选择弹窗：展示全部兼职人员，单选为 ta的团长 */}
      <Dialog header="选择 ta的团长" visible={leaderDlg} width="640px" closeOnOverlayClick onClose={() => setLeaderDlg(false)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button theme="default" variant="text" size="small" onClick={() => setLeaderDlg(false)}>取消</Button>
              <Button theme="primary" variant="text" size="small" onClick={() => { setLeaderDlg(false); }}>关闭</Button>
            </Space>
          </div>
        }>
        <p style={{ margin: '0 0 12px', color: '#888', fontSize: 12 }}>点击行选择，选中者即为该兼职人员的团长（登录账号存入 ta的团长）</p>
        <Table
          rowKey="id"
          resizable
          data={leaderOpts}
          loading={leaderLoading}
          hover
          size="small"
          columns={[
            { colKey: 'row-select', type: 'single', width: 40 },
            { colKey: 'name', title: '姓名', ellipsis: true, cell: ({ row }) => row.name || row.phone },
            { colKey: 'phone', title: '登录账号', ellipsis: true, cell: ({ row }) => row.phone || '-' },
            { colKey: 'role', title: '角色', width: 110, cell: ({ row }) => ROLE_LABEL[row.role] || row.role || '-' },
          ]}
          empty={<span>暂无兼职人员</span>}
          selectOnRowClick
          selectedRowKeys={form.leaderId == null ? [] : [Number(form.leaderId)]}
          onSelectChange={(keys) => {
            const k = keys && keys.length ? String(keys[0]) : '';
            const u = k ? leaderOpts.find((x) => String(x.id) === k) : null;
            if (u) chooseLeader(u);
          }}
        />
        {leaderTotal > LEADER_PAGE_SIZE ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Pagination
              total={leaderTotal}
              pageSize={LEADER_PAGE_SIZE}
              current={leaderPage}
              showJumper
              onChange={(info) => {
                const np = typeof info === 'number' ? info : ((info && info.current) || 1);
                if (np !== leaderPage) loadLeader(np);
              }}
            />
          </div>
        ) : null}
      </Dialog>

      {/* 负责客服：明道云员工选择弹窗（复用组件） */}
      <EmployeePicker
        visible={empDlg}
        title="选择客服"
        initial={form.serviceKfId ? { accountId: form.serviceKfId, fullname: form.serviceKf } : null}
        onCancel={() => setEmpDlg(false)}
        onSelect={(emp) => {
          setForm((f) => ({ ...f, serviceKf: emp.fullname, serviceKfId: String(emp.accountId) }));
          setEmpDlg(false);
        }}
      />
    </>
  );
}
